/**
 * Held, scheduled and undone sends, and open tracking (piece 32, Compose and
 * Open tracking; decision 0162): the undo delay a person sends under, the
 * routes that cancel or retry a message waiting in Outbox, and the open-tracking
 * link — an opaque token, never the message id, answered with the document and
 * nothing about the account.
 */
import { MAIL_API_PATH, MAIL_MESSAGES_PATH } from './client';
import { isMailHtml } from './signature';

/** Cancel a held or scheduled message before release, and try a refused one again. */
export const mailCancelPath = (id: string): string => `${MAIL_MESSAGES_PATH}/${id}/cancel`;
export const mailRetryPath = (id: string): string => `${MAIL_MESSAGES_PATH}/${id}/retry`;
/** The organisation's default undo delay, in Settings › Email › Sending. */
export const MAIL_SETTINGS_UNDO_DELAY_PATH = `${MAIL_API_PATH}/settings/undo-delay`;

/** 🔒 The undo delays a person chooses between, in seconds; zero sends at once (piece 32). */
export const UNDO_SEND_DELAYS = [0, 5, 10, 20, 30, 60] as const;
export const UNDO_SEND_MAX_SECONDS = 600;

/** The delay a person sends under: their own row over the organisation's, zero when neither is a usable number. */
export function undoDelayFrom(personValue: unknown, organizationValue: unknown): number {
  const usable = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= UNDO_SEND_MAX_SECONDS;
  if (usable(personValue)) return Math.floor(personValue);
  if (usable(organizationValue)) return Math.floor(organizationValue);
  return 0;
}

/** A scheduled send's release time, as an instant; null where none was asked for, refused where it is not in the future. */
export function scheduledReleaseProblem(sendAt: string | null | undefined, now: Date): string | null {
  if (!sendAt) return null;
  const at = new Date(sendAt);
  if (Number.isNaN(at.getTime())) return 'a scheduled send names a date and a time';
  if (at.getTime() <= now.getTime()) return 'a scheduled send is set for a time still to come';
  return null;
}

/** 🔒 The route a tracked send's link reaches, unauthenticated and internet-facing (piece 32; decision 0162). */
export const MAIL_OPEN_ROUTE = '/public/mail/open';
/** The address a customer's link names: the API's own route through the reverse proxy's `/api` path. */
export const MAIL_OPEN_LINK_PATH = `/api${MAIL_OPEN_ROUTE}`;
/** 🔨 The per-address and whole-route limits, on the lead intake's pattern: a stranger cannot hammer it. */
export const OPEN_TRACKING_PER_ADDRESS_PER_HOUR = 60;
export const OPEN_TRACKING_GLOBAL_PER_HOUR = 5000;

/** 🔨 An opaque token, never the message id: 32 characters of URL-safe base64. */
export function isTrackingToken(value: string): boolean {
  return /^[A-Za-z0-9_-]{32}$/.test(value);
}

export function trackingLink(appBaseUrl: string, token: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}${MAIL_OPEN_LINK_PATH}/${token}`;
}

/** The words the link reads in a tracked send, which the customer clicks to open the document. */
export const TRACKING_LINK_LABEL = 'View the document online';

/** The line a tracked send carries: the document, one click away, in the body's own form. */
export function withTrackingLink(body: string, url: string, label: string): string {
  if (isMailHtml(body)) return `${body}<p><a href="${url}">${label}</a></p>`;
  return `${body.replace(/\s+$/, '')}\n\n${label}: ${url}`;
}
